<script setup lang="ts">
import { computed, type PropType } from 'vue'

export interface SmartPickerOption {
  id: string | number
  title: string
  subtitle?: string
  meta?: string
}

const props = defineProps({
  open: { type: Boolean, required: true },
  title: { type: String, required: true },
  eyebrow: { type: String, required: true },
  searchLabel: { type: String, required: true },
  searchPlaceholder: { type: String, required: true },
  searchValue: { type: String, required: true },
  loading: { type: Boolean, default: false },
  errorMessage: { type: String, default: '' },
  emptyMessage: { type: String, required: true },
  items: {
    type: Array as PropType<SmartPickerOption[]>,
    default: () => [],
  },
  page: { type: Number, default: 1 },
  totalPages: { type: Number, default: 1 },
  pageLabel: { type: String, default: '' },
  closeAriaLabel: { type: String, required: true },
  retryLabel: { type: String, required: true },
  previousLabel: { type: String, required: true },
  nextLabel: { type: String, required: true },
  cancelLabel: { type: String, required: true },
})

const emit = defineEmits<{
  close: []
  retry: []
  previous: []
  next: []
  select: [item: SmartPickerOption]
  'update:searchValue': [value: string]
}>()

const canGoPrevious = computed(() => props.page > 1)
const canGoNext = computed(() => props.page < props.totalPages)
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="modal-backdrop" @click.self="emit('close')">
      <section class="picker-modal" role="dialog" aria-modal="true" :aria-label="title">
        <header>
          <div><p class="eyebrow">{{ eyebrow }}</p><h3>{{ title }}</h3></div>
          <button class="close-button" type="button" :aria-label="closeAriaLabel" @click="emit('close')">×</button>
        </header>
        <label class="search-label">{{ searchLabel }}
          <input
            :value="searchValue"
            autofocus
            type="search"
            :placeholder="searchPlaceholder"
            @input="emit('update:searchValue', ($event.target as HTMLInputElement).value)"
          >
        </label>
        <div v-if="loading" class="picker-state">
          <slot name="loading">Loading...</slot>
        </div>
        <div v-else-if="errorMessage" class="picker-state picker-state--error">
          <p>{{ errorMessage }}</p><button type="button" @click="emit('retry')">{{ retryLabel }}</button>
        </div>
        <div v-else-if="!items.length" class="picker-state">{{ emptyMessage }}</div>
        <div v-else class="picker-list">
          <button
            v-for="item in items"
            :key="item.id"
            type="button"
            class="picker-option"
            @click="emit('select', item)"
          >
            <span><strong>{{ item.title }}</strong><small v-if="item.subtitle">{{ item.subtitle }}</small></span>
            <span v-if="item.meta" class="picker-meta">{{ item.meta }}</span>
          </button>
        </div>
        <slot name="helper" />
        <footer>
          <span>{{ pageLabel }}</span>
          <div class="footer-actions">
            <button class="page-button" type="button" :disabled="!canGoPrevious" @click="emit('previous')">{{ previousLabel }}</button>
            <button class="page-button" type="button" :disabled="!canGoNext" @click="emit('next')">{{ nextLabel }}</button>
            <button class="secondary-button" type="button" @click="emit('close')">{{ cancelLabel }}</button>
          </div>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.modal-backdrop { position: fixed; z-index: 1000; inset: 0; display: grid; padding: 24px; background: rgb(5 31 67 / 52%); place-items: center; }
.picker-modal { display: grid; width: min(720px, 100%); max-height: min(680px, 90vh); gap: 12px; padding: 20px; border: 1px solid #cbd7e5; background: #fff; box-shadow: 0 24px 60px rgb(5 31 67 / 25%); }
header, footer { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
h3 { margin: 0; }
.eyebrow { margin: 0 0 4px; color: #66726e; font-size: 11px; font-weight: 800; text-transform: uppercase; }
.close-button { padding: 2px 10px; color: #66726e; background: transparent; font-size: 28px; }
.search-label { display: grid; gap: 6px; color: #53625d; font-size: 13px; font-weight: 700; }
input { padding: 11px 12px; border: 1px solid #aebdcc; font: inherit; }
.picker-list { display: grid; border: 1px solid #d7dfdc; overflow-y: auto; }
.picker-option { display: flex; align-items: center; justify-content: space-between; min-height: 48px; gap: 16px; padding: 7px 12px; border: 0; border-bottom: 1px solid #e1e7e5; color: #173454; background: #fff; text-align: left; }
.picker-option:last-child { border-bottom: 0; }
.picker-option:hover, .picker-option:focus-visible { border-color: #07996f; background: #effaf6; }
.picker-option span:first-child { display: grid; gap: 1px; }
.picker-option strong { font-size: 14px; line-height: 1.2; }
.picker-option small, .picker-meta, footer { color: #66726e; font-size: 12px; }
.picker-state { display: grid; min-height: 180px; color: #66726e; place-items: center; }
.picker-state--error { color: #852d22; }
button { border: 0; font: inherit; font-weight: 700; cursor: pointer; }
.footer-actions { display: flex; gap: 7px; }
.secondary-button, .page-button, .picker-state button { padding: 7px 11px; border: 1px solid #b9c5c1; color: #285b9a; background: #fff; }
.page-button:disabled { color: #9aa7a3; background: #f3f6f5; cursor: default; }
@media (max-width: 560px) { .modal-backdrop { padding: 10px; } .picker-option { align-items: flex-start; flex-direction: column; } }
</style>
